/**
 * PersonalizationForm — unified custom element for photo upload, AI generation,
 * name input, variant selection and add-to-cart in a single cohesive flow.
 *
 * Events consumed (window):
 *   kunstpfote:generation-complete  → show generated image, reveal order section
 *   kunstpfote:photo-removed        → remove entry added via photo-management modal
 *   kunstpfote:photos-uploaded      → add entries added via photo-management modal
 *
 * Events dispatched (window):
 *   kunstpfote:generation-started   → triggers the generation-progress modal
 *   kunstpfote:photo-removed        → notifies photo-management modal
 *   kunstpfote:photos-uploaded      → notifies other components after an own upload
 */
class PersonalizationForm extends HTMLElement {
  // ── Configuration ────────────────────────────────────────────────────────

  static SESSION_KEY    = 'kunstpfote_session';
  static UPLOAD_DATA_KEY = 'kunstpfote_uploads';
  static NAME_KEY       = 'kunstpfote_name';
  static MAX_FILES      = 4;
  static MAX_SIZE       = 10 * 1024 * 1024; // 10 MB
  static ALLOWED_TYPES  = ['image/jpeg', 'image/png', 'image/webp'];

  // ── Internal state ───────────────────────────────────────────────────────

  /** @type {Array<{id:string, file?:File, imgSrc:string, filename:string|null, state:'uploading'|'done'|'error'}>} */
  #entries = [];

  /** Serialises upload batches so sessionId is written before the next batch reads it */
  #uploadQueue = Promise.resolve();

  // ── Cached DOM references ────────────────────────────────────────────────

  #dropzone;
  #fileInput;
  #previewGrid;
  #generateBtn;
  #messagesEl;
  #orderSection;
  #lightbox;
  #lightboxImg;
  #nameToggle;
  #nameField;
  #namePropInput;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    this.#cacheElements();
    this.#initUpload();
    this.#initLightbox();
    this.#initVariantSync();
    this.#initNameSync();
    this.#initSubmitHandler();
    this.#initExternalEvents();
    this.#restoreSession();
    this.#syncSessionToCart();
    this.#initDownloadLogic();
  }

  // ── DOM references ───────────────────────────────────────────────────────

  #cacheElements() {
    this.#dropzone      = this.querySelector('.pf-dropzone');
    this.#fileInput     = this.querySelector('.pf-file-input');
    this.#previewGrid   = this.querySelector('.pf-preview-grid');
    this.#generateBtn   = this.querySelector('.pf-generate-btn');
    this.#messagesEl    = this.querySelector('.pf-messages');
    this.#orderSection  = this.querySelector('.pf-order');
    this.#lightbox      = this.querySelector('.pf-lightbox');
    this.#lightboxImg   = this.querySelector('.pf-lightbox__img');
    this.#nameToggle    = this.querySelector('.pf-name__toggle');
    this.#nameField     = this.querySelector('.pf-name__field');
    this.#namePropInput = this.querySelector('.pf-name-prop-input');
  }

  // ── Computed getters ─────────────────────────────────────────────────────

  get #uploadBase() {
    return this.dataset.uploadEndpoint.replace(/\/upload$/, '');
  }

  get #sku() {
    return this.dataset.sku || '';
  }

  get #sessionId() {
    return localStorage.getItem(PersonalizationForm.SESSION_KEY);
  }

  get #isGenerated() {
    return Boolean(this.#previewGrid?.querySelector('.pf-preview-item--generated'));
  }

  // ── Upload initialisation ────────────────────────────────────────────────

  #initUpload() {
    this.#dropzone.addEventListener('click', () => {
      if (!this.#dropzone.classList.contains('pf-dropzone--full')) {
        this.#fileInput.click();
      }
    });

    this.#dropzone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#fileInput.click();
      }
    });

    this.#fileInput.addEventListener('change', (e) => {
      this.#handleFiles(e.target.files);
      this.#fileInput.value = '';
    });

    this.#dropzone.addEventListener('dragenter', (e) => e.preventDefault());

    this.#dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      this.#dropzone.classList.add('pf-dropzone--active');
    });

    this.#dropzone.addEventListener('dragleave', (e) => {
      if (!this.#dropzone.contains(e.relatedTarget)) {
        this.#dropzone.classList.remove('pf-dropzone--active');
      }
    });

    this.#dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      this.#dropzone.classList.remove('pf-dropzone--active');
      this.#handleFiles(e.dataTransfer.files);
    });

    this.#generateBtn.addEventListener('click', () => this.#handleGenerate());
  }

  // ── File handling ────────────────────────────────────────────────────────

  #handleFiles(fileList) {
    this.#clearErrors();

    const remaining = PersonalizationForm.MAX_FILES - this.#entries.length;
    if (remaining <= 0) {
      this.#showError('Du hast bereits 4 Fotos hinzugefügt. Entferne ein Foto, um ein neues hochzuladen.');
      return;
    }

    const errors = [];
    const valid  = [];

    for (const file of Array.from(fileList)) {
      if (!PersonalizationForm.ALLOWED_TYPES.includes(file.type)) {
        errors.push(`„${file.name}": Nicht unterstütztes Format – bitte JPG, PNG oder WebP verwenden.`);
        continue;
      }
      if (file.size > PersonalizationForm.MAX_SIZE) {
        errors.push(`„${file.name}": Datei zu groß (max. 10 MB erlaubt).`);
        continue;
      }
      valid.push(file);
    }

    const toAdd = valid.slice(0, remaining);
    if (valid.length > remaining) {
      errors.push(`Maximal ${PersonalizationForm.MAX_FILES} Fotos erlaubt – ${valid.length - remaining} Foto(s) wurden übersprungen.`);
    }

    errors.forEach((msg) => this.#showError(msg));
    if (toAdd.length === 0) return;

    const newEntries = toAdd.map((file) => {
      const entry = {
        id:       crypto.randomUUID(),
        file,
        imgSrc:   URL.createObjectURL(file),
        filename: null,
        state:    'uploading',
      };
      this.#entries.push(entry);
      this.#addPreviewItem(entry);
      return entry;
    });

    this.#uploadQueue = this.#uploadQueue.then(() => this.#uploadBatch(newEntries));
    this.#updateDropzone();
  }

  // ── Upload ───────────────────────────────────────────────────────────────

  async #uploadBatch(entries) {
    const formData = new FormData();
    for (const entry of entries) formData.append('photos', entry.file);

    const sessionId = this.#sessionId;
    if (sessionId) formData.append('sessionId', sessionId);

    const sku = this.#sku;
    if (sku) formData.append('motiv', sku);

    try {
      const res = await fetch(this.dataset.uploadEndpoint, { method: 'POST', body: formData });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Serverfehler (${res.status})`);
      }

      const data         = await res.json();
      const newSessionId = data.sessionId;

      if (newSessionId) {
        localStorage.setItem(PersonalizationForm.SESSION_KEY, newSessionId);
        this.#syncSessionToCart();
      }

      data.files.forEach((filename, i) => {
        const entry = entries[i];
        if (!entry) return;
        entry.filename = filename;
        entry.imgSrc   = this.#buildImageUrl(newSessionId, 'originals', filename);
        entry.state    = 'done';
        this.#updatePreviewItem(entry);
      });

      this.#appendToStoredData(newSessionId, data.files);

      // Let other components (floating button, nav badge, …) pick up the new photos
      window.dispatchEvent(new CustomEvent('kunstpfote:photos-uploaded', {
        detail: { sessionId: newSessionId, files: data.files, uploadBase: this.#uploadBase },
      }));
    } catch (err) {
      for (const entry of entries) {
        entry.state = 'error';
        this.#updatePreviewItem(entry);
        if (entry.file) URL.revokeObjectURL(entry.imgSrc);
      }
      this.#showError(err.message || 'Upload fehlgeschlagen. Bitte versuche es erneut.');
    }

    this.#updateStatus();
  }

  #buildImageUrl(sessionId, type, filename) {
    return `${this.#uploadBase}/image/${sessionId}/${type}/${filename}`;
  }

  // ── Preview items ────────────────────────────────────────────────────────

  #addPreviewItem(entry) {
    const li = document.createElement('li');
    li.className  = 'pf-preview-item';
    li.dataset.id = entry.id;

    const isUploading = entry.state === 'uploading';
    li.innerHTML = `
      <img src="${entry.imgSrc}" class="pf-preview-item__img" alt="" loading="lazy">
      ${isUploading ? '<div class="pf-preview-item__overlay"><span class="pf-spinner"></span></div>' : ''}
      <button class="pf-preview-item__edit" type="button" aria-label="Fotos bearbeiten" title="Fotos bearbeiten">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>
    `;

    li.querySelector('.pf-preview-item__edit').addEventListener('click', () => {
      document.getElementById('photo-management-modal')?.showDialog?.();
    });

    if (!isUploading) {
      li.querySelector('.pf-preview-item__img')?.addEventListener(
        'error',
        () => this.#handleBrokenEntry(entry.id),
        { once: true }
      );
    }

    this.#previewGrid.appendChild(li);
  }

  #updatePreviewItem(entry) {
    const li = this.#previewGrid.querySelector(`[data-id="${entry.id}"]`);
    if (!li) return;

    const img = li.querySelector('.pf-preview-item__img');
    if (img && entry.imgSrc) img.src = entry.imgSrc;

    const getOrCreateOverlay = () => {
      let ov = li.querySelector('.pf-preview-item__overlay');
      if (!ov) {
        ov = document.createElement('div');
        li.insertBefore(ov, li.querySelector('.pf-preview-item__remove'));
      }
      return ov;
    };

    if (entry.state === 'done') {
      const ov = getOrCreateOverlay();
      ov.className = 'pf-preview-item__overlay pf-preview-item__overlay--done';
      ov.innerHTML = `
        <svg class="pf-checkmark" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
          <path d="M4 11L9 16L18 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      setTimeout(() => {
        ov.style.transition = 'opacity 0.4s ease';
        ov.style.opacity    = '0';
        setTimeout(() => ov.remove(), 400);
      }, 1400);
    } else if (entry.state === 'error') {
      const ov = getOrCreateOverlay();
      ov.className = 'pf-preview-item__overlay pf-preview-item__overlay--error';
      ov.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M2 2L16 16M16 2L2 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>`;
    }
  }

  async #removeEntry(id) {
    const idx = this.#entries.findIndex((e) => e.id === id);
    if (idx === -1) return;
    const entry = this.#entries[idx];

    if (entry.filename && entry.state === 'done') {
      const sessionId = this.#sessionId;
      if (sessionId) {
        const li  = this.#previewGrid.querySelector(`[data-id="${id}"]`);
        const btn = li?.querySelector('.pf-preview-item__remove');
        if (btn) btn.disabled = true;

        try {
          const url = `${this.#uploadBase}/image/${sessionId}/originals/${entry.filename}`;
          const res = await fetch(url, { method: 'DELETE' });
          if (!res.ok && res.status !== 404) {
            if (btn) btn.disabled = false;
            this.#showError(
              res.status === 429
                ? 'Zu viele Anfragen – bitte kurz warten.'
                : `Löschen fehlgeschlagen (${res.status}).`
            );
            return;
          }
        } catch {
          if (btn) btn.disabled = false;
          this.#showError('Löschen fehlgeschlagen. Bitte erneut versuchen.');
          return;
        }
      }
    }

    if (entry.file) URL.revokeObjectURL(entry.imgSrc);

    // Splice before dispatching so the external event handler finds idx === -1 and skips
    this.#entries.splice(idx, 1);
    this.#previewGrid.querySelector(`[data-id="${id}"]`)?.remove();
    this.#updateDropzone();
    this.#updateStatus();

    if (entry.filename) {
      this.#removeFromStoredData(entry.filename);
      window.dispatchEvent(new CustomEvent('kunstpfote:photo-removed', { detail: { filename: entry.filename } }));
    }
  }

  #handleBrokenEntry(id) {
    const idx = this.#entries.findIndex((e) => e.id === id);
    if (idx === -1) return;
    const entry = this.#entries[idx];
    if (entry.filename) this.#removeFromStoredData(entry.filename);
    this.#entries.splice(idx, 1);
    this.#previewGrid.querySelector(`[data-id="${id}"]`)?.remove();
    this.#updateDropzone();
    this.#updateStatus();
  }

  // ── Generated image ──────────────────────────────────────────────────────

  /** Called by generation-complete event after a new portrait is ready */
  #showGeneratedImage(imgUrl) {
    this.#clearGeneratedItem();
    this.#insertGeneratedItem(imgUrl, this.#sku);
  }

  #insertGeneratedItem(imgUrl, sku) {
    const li = document.createElement('li');
    li.className            = 'pf-preview-item pf-preview-item--generated';
    li.dataset.generatedSku = sku || '';
    li.innerHTML = `
      <img class="pf-preview-item__img" src="${imgUrl}" alt="Dein Kunstporträt">
      <button class="pf-preview-item__zoom" type="button" aria-label="Vorschau öffnen" title="Vorschau öffnen" data-clarity="Generiertes Porträt Vorschau öffnen">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      </button>
    `;

    li.querySelector('.pf-preview-item__zoom').addEventListener('click', (e) => {
      e.stopPropagation();
      this.#lightboxImg.src = imgUrl;
      this.#lightbox.showModal();
    });

    li.querySelector('.pf-preview-item__img').addEventListener('error', () => {
      if (sku) {
        const data = this.#readStoredData();
        if (data) {
          data.generated = (data.generated ?? []).filter((e) => this.#skuFromEntry(e) !== sku);
          this.#writeStoredData(data);
        }
      }
      li.remove();
      this.#updateGridModifier();
      this.#setOrderSectionVisible(false);
    }, { once: true });

    this.#previewGrid.prepend(li);
    this.#updateGridModifier();
    this.#setOrderSectionVisible(true);
    this.#dropzone.hidden = true;
    this.#updateStatus();
  }

  #clearGeneratedItem() {
    this.#previewGrid.querySelector('.pf-preview-item--generated')?.remove();
    this.#updateGridModifier();
    this.#setOrderSectionVisible(false);
    this.#dropzone.hidden = false;
    this.#updateStatus();
  }

  /** On page load or variant change: check localStorage and probe server */
  #restoreGeneratedImage() {
    this.#clearGeneratedItem();

    const sku = this.#sku;
    if (!sku) return;
    const data = this.#readStoredData();
    if (!data?.sessionId) return;

    const imgUrl = this.#buildImageUrl(data.sessionId, 'generated', `${sku}.jpg`);
    const stored = this.#normaliseGeneratedSkus(data.generated);

    if (stored.includes(sku)) {
      this.#insertGeneratedItem(imgUrl, sku);
    } else {
      // Probe the server – covers sessions generated before localStorage tracking was added
      const probe = new Image();
      probe.onload = () => {
        const fresh = this.#readStoredData();
        if (fresh) {
          fresh.generated = fresh.generated ?? [];
          if (!this.#normaliseGeneratedSkus(fresh.generated).includes(sku)) {
            fresh.generated.push({ sku, url: window.location.pathname + window.location.search });
          }
          this.#writeStoredData(fresh);
        }
        this.#insertGeneratedItem(imgUrl, sku);
      };
      probe.src = `${imgUrl}?_t=${Date.now()}`;
    }
  }

  // ── Order section visibility ─────────────────────────────────────────────

  #setOrderSectionVisible(visible) {
    if (!this.#orderSection) return;
    this.#orderSection.hidden = !visible;
  }

  // ── Session persistence ──────────────────────────────────────────────────

  #readStoredData() {
    try {
      const raw = localStorage.getItem(PersonalizationForm.UPLOAD_DATA_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data.sessionId || !Array.isArray(data.originals)) return null;
      return data;
    } catch {
      return null;
    }
  }

  #writeStoredData(data) {
    if (!data.uploadBase) data.uploadBase = this.#uploadBase;
    localStorage.setItem(PersonalizationForm.UPLOAD_DATA_KEY, JSON.stringify(data));
  }

  #appendToStoredData(sessionId, filenames) {
    const existing = this.#readStoredData() ?? {};
    this.#writeStoredData({
      ...existing,
      sessionId,
      originals: [...(existing.originals ?? []), ...filenames],
    });
  }

  #removeFromStoredData(filename) {
    const data = this.#readStoredData();
    if (!data) return;
    data.originals = data.originals.filter((f) => f !== filename);
    this.#writeStoredData(data);
  }

  /** Proactively write the session to the Shopify cart so it becomes an order attribute */
  #syncSessionToCart() {
    const sessionId = this.#sessionId;
    if (!sessionId) return;
    fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ attributes: { kunstpfote_session: sessionId } }),
    }).catch(() => {});
  }

  // ── Variant sync ─────────────────────────────────────────────────────────

  #initVariantSync() {
    document.addEventListener('variant:update', (e) => {
      const fullSku = e.detail?.resource?.sku;
      if (fullSku == null) return;

      // Mirror the truncation used by the generation server (first two dash-parts)
      const parts     = fullSku.split('-');
      const truncated = parts.length >= 2 ? `${parts[0]}-${parts[1]}` : fullSku;

      this.dataset.sku = truncated;
      const skuInput   = this.querySelector('.pf-sku-input');
      if (skuInput) skuInput.value = truncated;

      this.#restoreGeneratedImage();
      this.#applyDownloadRules();
    });
  }

  // ── Download variant logic ───────────────────────────────────────────────
  // Attach a change listener directly on the picker — variant:update is
  // unreliable here because clicking "Download" may not resolve to a valid
  // variant until after 4K is auto-selected (so the event never fires or
  // fires without a SKU, triggering the early-return in #initVariantSync).

  #initDownloadLogic() {
    const picker = this.querySelector('variant-picker');
    if (!picker) return;

    // Only react to real user interactions (isTrusted).
    // Defer with setTimeout so variant-picker finishes its own synchronous
    // handler first — otherwise we act on stale DOM state on mobile.
    picker.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && e.isTrusted) {
        setTimeout(() => this.#applyDownloadRules(), 0);
      }
    });

    this.#applyDownloadRules();
  }
  // When "Download" material is selected: hide size picker + auto-select "4K".
  // For all other materials: show size picker, hide the "4K" option, and
  // auto-select the first non-4K size if "4K" is currently checked.

  #applyDownloadRules() {
    const picker = this.querySelector('variant-picker');
    if (!picker) return;

    // The size fieldset is identified by containing a "4K" input
    const sizeFieldset = Array.from(picker.querySelectorAll('fieldset'))
      .find((fs) => fs.querySelector('input[value="4K"]'));
    if (!sizeFieldset) return;

    const isDownload = Boolean(
      picker.querySelector('fieldset.variant-option--images input[value="Download"]:checked')
    );

    this.#applyDeliveryText(isDownload);

    if (isDownload) {
      // Select 4K first (while the fieldset is still visible), then hide it.
      // Hiding before selecting breaks .click() on mobile Safari.
      const fourKInput = sizeFieldset.querySelector('input[value="4K"]');
      if (fourKInput && !fourKInput.checked) {
        fourKInput.checked = true;
        fourKInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      sizeFieldset.hidden = true;
    } else {
      sizeFieldset.hidden = false;

      // Keep "4K" out of sight for non-download materials
      const fourKLabel = sizeFieldset.querySelector('input[value="4K"]')?.closest('label');
      if (fourKLabel) fourKLabel.hidden = true;

      // Select the first available non-4K size if the current selection is missing,
      // is "4K", or is no longer available for this material
      const checked = sizeFieldset.querySelector('input[type="radio"]:checked');
      const invalid = !checked
        || checked.value === '4K'
        || checked.dataset.optionAvailable === 'false';

      if (invalid) {
        const first = sizeFieldset.querySelector(
          'input[type="radio"]:not([value="4K"])[data-option-available="true"]'
        );
        if (first && !first.checked) {
          first.checked = true;
          first.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  /** Swaps the "Lieferzeit: X Wochen" line for an instant-availability note when Download is selected */
  #applyDeliveryText(isDownload) {
    const physicalText = this.querySelector('.pf-delivery__text--physical');
    const downloadText = this.querySelector('.pf-delivery__text--download');
    const physicalIcon = this.querySelector('.pf-delivery__icon--physical');
    const downloadIcon = this.querySelector('.pf-delivery__icon--download');

    if (physicalText) physicalText.hidden = isDownload;
    if (downloadText) downloadText.hidden = !isDownload;
    if (physicalIcon) physicalIcon.hidden = isDownload;
    if (downloadIcon) downloadIcon.hidden = !isDownload;
  }

  // ── Name field → hidden property + localStorage ─────────────────────────
  // The typed name itself is stored globally (like the uploaded photos) so it
  // carries over and pre-fills on any other product with a name field. Whether
  // a name is actually wanted is decided per product via the checkbox – if
  // it's off, "noname" is sent instead (both as properties[Name] and to
  // /generate) so the backend can tell "declined" apart from "field is empty".

  #initNameSync() {
    if (!this.#nameField || !this.#namePropInput) return;

    const saved = localStorage.getItem(PersonalizationForm.NAME_KEY);
    if (saved) {
      this.#nameField.value = saved;
      if (this.#nameToggle) this.#nameToggle.checked = true;
    }
    this.#namePropInput.value = this.#currentName();

    this.#nameField.addEventListener('input', () => {
      const value = this.#nameField.value.trim();

      if (value) {
        localStorage.setItem(PersonalizationForm.NAME_KEY, value);
      } else {
        localStorage.removeItem(PersonalizationForm.NAME_KEY);
      }

      this.#namePropInput.value = this.#currentName();
    });

    this.#nameToggle?.addEventListener('change', () => {
      this.#namePropInput.value = this.#currentName();
    });
  }

  /** Resolves what should actually be submitted: the typed name, or "noname" if declined/empty */
  #currentName() {
    if (!this.#nameField) return ''; // product has no name option at all
    const wantsName = this.#nameToggle ? this.#nameToggle.checked : true;
    const value = this.#nameField.value.trim();
    return (wantsName && value) ? value : 'noname';
  }

  // ── Form submit handler ──────────────────────────────────────────────────

  #initSubmitHandler() {
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    // Capture phase: ensure cart attribute is synced before the add-to-cart request fires
    form.addEventListener('submit', () => this.#syncSessionToCart(), true);
  }

  // ── Generate ─────────────────────────────────────────────────────────────

  #handleGenerate() {
    const sessionId = this.#sessionId;
    if (!sessionId) {
      this.#showError('Keine aktive Sitzung – bitte zuerst Fotos hochladen.');
      return;
    }

    this.#generateBtn.disabled = true;

    window.dispatchEvent(new CustomEvent('kunstpfote:generation-started', {
      detail: {
        sessionId,
        sku:        this.#sku,
        uploadBase: this.#uploadBase,
        productUrl: window.location.pathname + window.location.search,
        name:       this.#currentName(),
      },
    }));
  }

  // ── UI state ─────────────────────────────────────────────────────────────

  #updateDropzone() {
    const isFull = this.#entries.length >= PersonalizationForm.MAX_FILES;
    this.#dropzone.classList.toggle('pf-dropzone--full', isFull);
  }

  #updateStatus() {
    if (this.#isGenerated) {
      this.#generateBtn.hidden = true;
      return;
    }

    const uploading = this.#entries.some((e) => e.state === 'uploading');
    const done      = this.#entries.filter((e) => e.state === 'done');
    const hasSku    = Boolean(this.#sku);

    if (uploading) {
      this.#generateBtn.hidden   = true;
      this.#generateBtn.disabled = false;
      return;
    }

    this.#generateBtn.hidden   = !(done.length > 0 && hasSku);
    this.#generateBtn.disabled = false;
  }

  #updateGridModifier() {
    const has = Boolean(this.#previewGrid.querySelector('.pf-preview-item--generated'));
    this.#previewGrid.classList.toggle('pf-preview-grid--has-generated', has);
  }

  // ── Messages ─────────────────────────────────────────────────────────────

  #showError(msg) {
    const p = document.createElement('p');
    p.className   = 'pf-message pf-message--error';
    p.textContent = msg;
    this.#messagesEl.appendChild(p);
  }

  #clearErrors() {
    this.#messagesEl.querySelectorAll('.pf-message--error').forEach((el) => el.remove());
  }

  // ── Lightbox ─────────────────────────────────────────────────────────────

  #initLightbox() {
    this.#lightbox?.addEventListener('click', (e) => {
      if (e.target === this.#lightbox) this.#lightbox.close();
    });
    this.querySelector('.pf-lightbox__close')?.addEventListener('click', () => this.#lightbox.close());
  }

  // ── Session restore on page load ─────────────────────────────────────────

  #restoreSession() {
    const data = this.#readStoredData();
    if (!data || data.originals.length === 0) return;

    for (const filename of data.originals) {
      const entry = {
        id:       crypto.randomUUID(),
        imgSrc:   this.#buildImageUrl(data.sessionId, 'originals', filename),
        filename,
        state:    'done',
      };
      this.#entries.push(entry);
      this.#addPreviewItem(entry);
    }

    this.#updateDropzone();
    this.#updateStatus();
    this.#restoreGeneratedImage();
  }

  // ── External events (photo-management modal) ─────────────────────────────

  #initExternalEvents() {
    window.addEventListener('kunstpfote:generation-complete', (e) => {
      const { sku: generatedSku, sessionId, uploadBase } = e.detail;
      if (!generatedSku || generatedSku !== this.#sku) return;
      this.#generateBtn.disabled = false;
      this.#showGeneratedImage(`${uploadBase}/image/${sessionId}/generated/${generatedSku}.jpg`);
    });

    window.addEventListener('kunstpfote:photo-removed', (e) => {
      const { filename } = e.detail;
      const idx = this.#entries.findIndex((entry) => entry.filename === filename);
      if (idx === -1) return;
      const entry = this.#entries[idx];
      if (entry.file) URL.revokeObjectURL(entry.imgSrc);
      this.#entries.splice(idx, 1);
      this.#previewGrid.querySelector(`[data-id="${entry.id}"]`)?.remove();
      this.#updateDropzone();
      this.#updateStatus();
    });

    window.addEventListener('kunstpfote:generated-removed', (e) => {
      if (e.detail?.sku === this.#sku) {
        this.#clearGeneratedItem();
      }
    });

    window.addEventListener('kunstpfote:photos-uploaded', (e) => {
      const { sessionId, files, uploadBase } = e.detail;
      for (const filename of files) {
        if (this.#entries.some((entry) => entry.filename === filename)) continue;
        const entry = {
          id:       crypto.randomUUID(),
          imgSrc:   `${uploadBase}/image/${sessionId}/originals/${filename}`,
          filename,
          state:    'done',
        };
        this.#entries.push(entry);
        this.#addPreviewItem(entry);
      }
      this.#updateDropzone();
      this.#updateStatus();
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  #skuFromEntry(entry) {
    if (typeof entry === 'string') return entry.replace(/\.jpg$/i, '') || null;
    return (entry && typeof entry.sku === 'string') ? entry.sku : null;
  }

  #normaliseGeneratedSkus(arr) {
    return (arr ?? [])
      .map((e) => this.#skuFromEntry(e))
      .filter((s) => s && s !== 'undefined');
  }
}

customElements.define('personalization-form-component', PersonalizationForm);
